import { Component, effect, ElementRef, inject, Input } from '@angular/core';
import { SurveyRenderControllerStore } from '../render-store/render.store';
import { UnifiedSurveyStore } from '../render-store/unified-survey.store';
import { SafeScriptComponent } from '../script-injector/safe-script/safe-script.component';
import type { ISurveyPlugin } from './plugin.interface';
import { PluginHookName } from './plugin.interface';
import { JsonPipe } from '@angular/common';

@Component({
  selector: 'lib-plugin-host',
  imports: [SafeScriptComponent, JsonPipe],
  templateUrl: './plugin-host.html',
  styleUrl: './plugin-host.css',
})
export class PluginHost {
  @Input({ required: true }) pluginDef!: ISurveyPlugin;

  renderStore = inject(SurveyRenderControllerStore);
  surveyBotStore = inject(UnifiedSurveyStore);
  private el = inject(ElementRef);

  public safeHtml: string = '';
  public isPluginValid = true;
  public validationMessage = '';

  // Scoped storage for the plugin logic to keep custom variables between hooks
  private pluginInternalState: any = {};

  constructor() {
    // Re-run logic if survey data changes
    effect(() => {
      const registry = this.renderStore.registry();
      this.triggerHook('onDataChange', registry);
    });
  }

  ngOnInit() {
    console.log('Called');
    this.safeHtml = this.pluginDef.html || '';

    const isCompleted = this.surveyBotStore
      .completedPlugins()
      .includes(this.pluginDef.id);

    if (this.pluginDef.runOnce && isCompleted) {
      console.log(
        `Plugin ${this.pluginDef.id} is set to run once and has already completed.`,
      );
      return;
    }

    // Small delay to ensure innerHTML is rendered before running init logic
    setTimeout(() => this.triggerHook('onPageInit'), 0);
  }

  public triggerHook(hookName: PluginHookName, payload: any = null) {
    const script = this.pluginDef.hooks?.[hookName];
    if (!script) return;

    // Reset validation state before running submit hooks
    if (hookName === 'onBeforeSubmit') {
      this.isPluginValid = true;
      this.validationMessage = '';
    }

    const sessionId = this.surveyBotStore.sessionId();
    const surveyId = this.surveyBotStore.context();

    const context = {
      config: this.pluginDef.config,
      registry: this.renderStore.registry(),
      container: this.el.nativeElement,
      pageId: this.renderStore.currentPageState()?.page?.id,
      survey_context: {
        survey_id: surveyId,
        survey_session_id: sessionId,
      },

      // State persistence across hooks for this plugin instance
      ...this.pluginInternalState,

      // Helpers
      setInvalid: (msg: string) => {
        this.isPluginValid = false;
        this.validationMessage = msg;
      },
      setVariable: (key: string, val: any) => {
        this.surveyBotStore.updateMetadata(`${this.pluginDef.id}_${key}`, val);
      },
      getAnswer: (id: string) => this.renderStore.registry()[id]?.data,
    };

    try {
      const fn = new Function('context', 'payload', script);
      fn(context, payload);

      // Save state back
      delete context.config;
      delete context.registry;
      delete context.container;
      this.pluginInternalState = context;

      if (this.pluginDef.runOnce && hookName === 'onBeforeSubmit') {
        this.surveyBotStore.markPluginAsCompleted(this.pluginDef.id);
      }
    } catch (e) {
      console.error(`Plugin Execution Error [${this.pluginDef.id}]:`, e);
    }
  }

  ngOnDestroy() {
    this.triggerHook('onCleanup');
  }

  public runSubmitHook() {
    this.triggerHook('onBeforeSubmit');
  }
}
